import { Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { CollectorsListHeader } from '@/features/integrations/collectors/components/list-header'
import { CollectorsListSkeleton } from '@/features/integrations/collectors/components/skeletons'
import { collectorQueries } from '@/features/integrations/collectors/queries'
import { lazyNamed } from '@/utils/lazy-named'

import { getCurrentTenant } from '@/shared/auth/store'

import { ErrorBoundary } from '@/components/common/error-boundary'

const CollectorsList = lazyNamed(
	() => import('@/features/integrations/collectors/components/list'),
	'CollectorsList',
)

/**
 * `status` — the active status tab (`connected` | `degraded` | `error` |
 * `offline`), so "show me what's offline" is a link someone can send.
 *
 * Validated only as a string, with no parse-time default: absent means the
 * unfiltered view, and an unknown value degrades to it at read time
 * (`resolveCollectorStatusFilter`) rather than 400-ing a pasted link.
 */
const collectorsSearchSchema = z.object({
	status: z.string().optional(),
})

export const Route = createFileRoute('/_dashboard/integrations/collectors/')({
	validateSearch: zodValidator(collectorsSearchSchema),

	loader: ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		// Prefetch what the list suspends on, so navigation renders a warm cache
		// instead of waterfalling on first paint (A-07). Volume in parallel, not
		// after: it is a separate request the page never waits on.
		if (tenant) {
			queryClient.prefetchQuery(collectorQueries.summaryList(tenant))
			queryClient.prefetchQuery(collectorQueries.fleetVolume(tenant))
		}
	},

	head: () => ({
		meta: [{ title: 'Network Collectors | Integrations | BitLyft AIR®' }],
	}),

	component: CollectorsPage,
	pendingComponent: CollectorsPendingPage,
	errorComponent: RouteErrorComponent,
})

function CollectorsPage() {
	return (
		<>
			<CollectorsListHeader />

			<ErrorBoundary feature="integrations" section="collectors-list">
				<Suspense fallback={<CollectorsListSkeleton />}>
					<CollectorsList />
				</Suspense>
			</ErrorBoundary>
		</>
	)
}

function CollectorsPendingPage() {
	return (
		<>
			<CollectorsListHeader />
			<CollectorsListSkeleton />
		</>
	)
}
